import React, { useCallback, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import { Container } from '@mui/system';
import { EsContestEngageContainer } from '../../components/ScreenContainer/EsContestEngageContainer';
import { batch, useDispatch, useSelector } from 'react-redux';
import { loadingSelectedContestMetricsSelector, selectedContestMetricsSelector } from '../../selectors/selectors';
import { useParams } from 'react-router-dom';
import {
    getAwardCategoriesForContest,
    getContestAwards,
    getUserContest,
    publishContest,
    sendContestEmail,
    sendWinnersEmail,
    unPublishContest,
} from '../../redux/contest/actions';
import { getPlaylist, setmedia } from '../../redux/project/actions';
import { ISubmission } from '../../redux/discovery/types';
import { IProject } from '../../types/global/helper';
import { TContestFilter } from '../../redux/contest/types';
import moment from 'moment';
import { IContestStatus } from '../../types/global/generic.types';
import { ViewContestShimmer } from '../ViewContestShimmer';
import { contestAwardCategoryAndAwardsSelector } from '../../components/AvatarGroup/selector';
import { isEmpty } from '../../utils/lodash';
import { IFormState } from '../../components/EsContestSubmission/EsContestSubmission';

const styles = StyleSheet.create({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
});
/**
 *
 * @returns JSX.Element
 * contest-element
 */

interface IManageCampaign {
    onClose?: () => void;
}

const ManageCampaign: React.FC<IManageCampaign> = ({ onClose }) => {
    const params = useParams();
    const dispatch = useDispatch();
    const { contestId } = params;
    const selectedContest = useSelector(selectedContestMetricsSelector);
    const loading = useSelector(loadingSelectedContestMetricsSelector);
    const { contestAward } = useSelector(contestAwardCategoryAndAwardsSelector);

    // DANGER: Only send this when you are absolutely sure of your selection
    const canSendWinnerEmail = () =>
        (!selectedContest?.winner_email_sent || selectedContest.winner_email_sent === undefined) &&
        !isEmpty(contestAward);

    const handleGetContest = useCallback(() => {
        if (contestId) {
            batch(() => {
                dispatch(
                    getUserContest({
                        contestId: contestId,
                    }),
                );
                //TODO See if we can change this to be fetched in one call with getUserContest
                dispatch(
                    getAwardCategoriesForContest({
                        documentId: contestId,
                    }),
                );
                dispatch(
                    getContestAwards({
                        documentId: contestId,
                    }),
                );
            });
        }
    }, [contestId, dispatch]);

    useEffect(() => {
        handleGetContest();
    }, [handleGetContest]);

    const handleViewAllClicked = () => {
        dispatch(
            getPlaylist({
                id: selectedContest?.playlist[0] || '',
                withNavigate: false,
                skipAuthentication: true,
            }),
        );
    };

    const handleSelectSubmission = (submission: ISubmission) => {
        dispatch(
            setmedia({
                media: submission.project as unknown as IProject,
                mediaType: 'act',
            }),
        );
    };

    const handleFilterSelect = (filter: string) => {
        let currentDate = moment();
        let lastMonthDate = currentDate.subtract(1, 'months');
        if (selectedContest) {
            dispatch(
                getUserContest({
                    contestId: selectedContest?._id,
                    filter: filter.toLowerCase() as TContestFilter,
                    end: moment().toString(),
                    start: lastMonthDate.toString(),
                    skipRefresh: true,
                }),
            );
        }
    };

    const handleContestStatusChange = (status: IContestStatus) => {
        if (selectedContest) {
            if (status === 'draft') {
                dispatch(
                    unPublishContest({
                        documentId: selectedContest?._id,
                    }),
                );
            } else {
                dispatch(
                    publishContest({
                        documentId: selectedContest?._id,
                    }),
                );
            }
        }
    };

    const handleSendUserWinnerEmail = (is_test?: boolean) => {
        if (selectedContest) {
            dispatch(
                sendWinnersEmail({
                    contestId: selectedContest._id,
                    is_test,
                }),
            );
        }
    };

    const onSendContestEmail = (formData: IFormState) => {
        if (selectedContest) {
            dispatch(
                sendContestEmail({
                    contestId: selectedContest._id,
                    subject: formData.subject,
                    image: formData.image,
                    message: formData.message,
                    type: formData.type,
                    audienceScope: formData.audienceScope || 'participants',
                    doNotRepeat: formData.doNotRepeat !== false,
                    largeHeroImage: Boolean(formData.largeHeroImage),
                    ...(formData &&
                        formData.testUsers &&
                        formData.testUsers?.length > 0 && { testUsers: formData.testUsers }),
                }),
            );
        }
    };

    return (
        <Container disableGutters style={styles.container} maxWidth={false}>
            {loading && <ViewContestShimmer />}
            {selectedContest && !loading && (
                <EsContestEngageContainer
                    contest={selectedContest}
                    onClickViewAll={handleViewAllClicked}
                    onClickPlay={handleSelectSubmission}
                    handleFilterSelect={handleFilterSelect}
                    onToggleStatus={handleContestStatusChange}
                    canSendWinnerEmail={canSendWinnerEmail()}
                    onSendWinnersEmail={handleSendUserWinnerEmail}
                    onSendContestEmail={onSendContestEmail}
                    onClose={onClose}
                />
            )}
        </Container>
    );
};

export { ManageCampaign };
